import { formType, adminListingType } from '@propTypes';
import PropTypes from 'prop-types';
import { Form } from '@ant-design/compatible';
import { AdminListing } from '@root/types';
import { useTranslation } from 'react-i18next';
import Button from '@components/shared/V2Button';
import BuildingDetailsSection from './BuildingDetailsSection';
import ListingDetailsSection from './ListingDetailsSection';
import AdditionalDataSection from './AdditionalDataSection/index';
import s from './EditListing.module.less';

const formItemLayout = {
  labelCol: {
    sm: { span: 5 },
  },
  wrapperCol: {
    sm: { span: 10 },
  },
};

type Props = {
  listing: AdminListing;
  form: any; // antd3 form, not worth typing
  fetchListing: () => void;
  updateListing: (
    listingId: string,
    body: {},
    message: string,
    successCallback?: () => void,
  ) => void;
};

const ListingForm = ({ listing, form, updateListing, fetchListing }: Props) => {
  const { t } = useTranslation('admin');
  const { floor } = form.getFieldsValue();

  return (
    <>
      <Form
        className={s.form}
        {...formItemLayout}
        onSubmit={e => {
          e.preventDefault();
          const {
            breakOutSpace,
            breakOutSpaceDataSource,
            buildOutLevel,
            buildOutLevelDataSource,
            drinkingFountainCount,
            drinkingFountainCountDataSource,
            electricalRoom,
            electricalRoomDataSource,
            elevatorLocation,
            elevatorLocationDataSource,
            exclusive,
            exteriorSignage,
            exteriorSignageDataSource,
            finishedCeilingHeight,
            finishedCeilingHeightMagnitude,
            finishedCeilingHeightMeasure,
            finishedCeilingHeightUnit,
            furnished,
            furnishedDataSource,
            interiorSignage,
            interiorSignageDataSource,
            keyCardSpaceEntry,
            keyCardSpaceEntryDataSource,
            leaseType,
            maximumCapacityPersons,
            mechanicalRoom,
            mechanicalRoomDataSource,
            notMarketable,
            passengerElevatorCount,
            passengerElevatorCountDataSource,
            phoneBooths,
            phoneBoothsDataSource,
            privateOutdoorSpace,
            privateOutdoorSpaceDataSource,
            publicPreview,
            serviceElevatorCount,
            serviceElevatorCountDataSource,
            sharedFloorBathroomStallCount,
            sharedFloorBathroomStallCountDataSource,
            sharedPantry,
            sharedPantryDataSource,
            slabToSlabHeight,
            slabToSlabHeightMagnitude,
            slabToSlabHeightMeasure,
            slabToSlabHeightUnit,
            status,
            suite,
            windowSidesCount,
            windowSidesCountDataSource,
          } = form.getFieldsValue();

          const spaceMetadata = {
            breakOutSpace,
            breakOutSpaceDataSource,
            buildOutLevel,
            buildOutLevelDataSource,
            drinkingFountainCount,
            drinkingFountainCountDataSource,
            electricalRoom,
            electricalRoomDataSource,
            elevatorLocation,
            elevatorLocationDataSource,
            exteriorSignage,
            exteriorSignageDataSource,
            furnished,
            furnishedDataSource,
            interiorSignage,
            interiorSignageDataSource,
            keyCardSpaceEntry,
            keyCardSpaceEntryDataSource,
            mechanicalRoom,
            mechanicalRoomDataSource,
            passengerElevatorCount,
            passengerElevatorCountDataSource,
            phoneBooths,
            phoneBoothsDataSource,
            privateOutdoorSpace,
            privateOutdoorSpaceDataSource,
            serviceElevatorCount,
            serviceElevatorCountDataSource,
            sharedFloorBathroomStallCount,
            sharedFloorBathroomStallCountDataSource,
            sharedPantry,
            sharedPantryDataSource,
            windowSidesCount,
            windowSidesCountDataSource,
          };

          const formValues = {
            floor,
            status,
            suite,
            exclusive,
            slabToSlabHeight,
            slabToSlabHeightMagnitude,
            slabToSlabHeightUnit,
            slabToSlabHeightMeasure,
            finishedCeilingHeight,
            finishedCeilingHeightMagnitude,
            finishedCeilingHeightUnit,
            finishedCeilingHeightMeasure,
            leaseType,
            maximumCapacityPersons,
            notMarketable,
            publicPreview,
            space_metadata: spaceMetadata,
          };

          updateListing(listing.id, formValues, t('listing.updateSuccess'), form.resetFields);
        }}
      >
        <BuildingDetailsSection listing={listing} form={form} />
        <ListingDetailsSection listing={listing} form={form} />
        <AdditionalDataSection listing={listing} form={form} fetchListing={fetchListing} />
        <Form.Item>
          <Button htmlType="submit" disabled={!floor}>
            {t('listing.save')}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

ListingForm.propTypes = {
  listing: adminListingType.isRequired,
  form: formType.isRequired,
  updateListing: PropTypes.func.isRequired,
  fetchListing: PropTypes.func.isRequired,
};

export default Form.create({ name: 'listing' })(ListingForm);
